import { Link, useNavigate, useParams } from "react-router";
import { useEffect, useState, type FormEvent } from "react";
import { clockHm, eachDateInclusive, formatDateLabel, formatDurationPretty, formatLongDate, minutesAgoLabel, todayInZone } from "@shared/time.ts";
import { bookingDisplayTitle } from "@shared/upcoming.ts";
import { ideasForDate, itemsForDate, stayNightLabel } from "@shared/itinerary.ts";
import { composeTravelDay, isTravelDay, travelDayLabel } from "@shared/travel-day.ts";
import { humanStatus, UNKNOWN_FACT } from "@shared/labels.ts";
import { displayBookingStatus, timingConfidenceMessage, visibleEventNotes } from "@shared/plan-status.ts";
import { formatMinor } from "@shared/money.ts";
import { priceBasisSummary } from "@shared/price-basis.ts";
import { useTrip, type SaveResult } from "../../app/trip.tsx";
import { useSession } from "../../app/session.tsx";
import { Photo } from "../../components/Photo.tsx";
import { StatusNote } from "../../components/StatusNote.tsx";
import { StatusChip } from "../../components/StatusChip.tsx";
import { FactRow } from "../../components/FactRow.tsx";
import { DocumentList } from "../../components/DocumentList.tsx";
import { DateStrip } from "../../components/DateStrip.tsx";
import { TimelineItem } from "../../components/TimelineItem.tsx";
import { PlaceActions } from "../../components/PlaceActions.tsx";
import { DriverCard } from "../../components/DriverCard.tsx";
import { AllergyCard } from "../../components/AllergyCard.tsx";
import { AddPlanChooser } from "../../components/AddPlanChooser.tsx";
import { SmartBack, DayNav } from "../../components/SmartBack.tsx";
import { airlineStatusUrl, copyText, moneyLabel, whatsappDraftUrl } from "../../lib/format.ts";
import { applyPlanBooking, draftFromBooking } from "@shared/plan-booking.ts";
import { previewEventRemoval, removeEventFromTrip } from "@shared/plan-lifecycle.ts";
import { fetchLive } from "../../lib/api.ts";
import { filesFor, linkedTransfer, placeById } from "../../lib/records.ts";
import { flightObservation, type LivePayload } from "../../lib/live.ts";
import type { TripDocument } from "@shared/schema.ts";
import { PlanBookingFields } from "../editor/PlanBookingFields.tsx";
import { RemoveRecord } from "../../components/RemoveRecord.tsx";

function Back({ to = "/journey" }: { to?: string }) {
  return <SmartBack fallback={to} />;
}

export function StayPage() {
  const { id } = useParams();
  const { trip } = useTrip();
  const { role } = useSession();
  const stay = trip?.stays.find((s) => s.id === id);
  const place = trip?.places.find((p) => p.id === stay?.placeId);
  if (!trip || !stay || !place) return <main className="page"><Back /><p>Stay not found.</p></main>;
  const rooms = trip.bookings.filter((b) => b.stayId === stay.id);
  const dates = eachDateInclusive(stay.startDate, stay.endDate);
  const photo = stay.chapter === "Hang Dong" ? "hang-dong" : stay.chapter === "Koh Samet" ? "koh-samet" : stay.chapter === "Bangkok" ? "bangkok" : "chiang-mai";
  const splitHouseholds = trip.households.filter((h) => rooms.filter((r) => r.householdId === h.id).length > 1);
  const continuity = stay.roomMoveRequired === true
    ? "A room move is recorded."
    : stay.roomMoveRequired === false
      ? "The same room continues through this stay."
      : "Room continuity to confirm.";

  return (
    <main className="page">
      <Back />
      <div className="hero" style={{ marginTop: 12 }}>
        <Photo assetId={photo} alt={`${place.name} destination photograph`} />
        <div className="hero-copy">
          <p className="kicker">{stay.chapter}</p>
          <h1 className="serif" style={{ margin: "6px 0" }}>{place.name}</h1>
          <p>{formatDateLabel(stay.startDate)} – {formatDateLabel(stay.endDate)} · {stay.nights} {stay.nights === 1 ? "night" : "nights"}</p>
        </div>
      </div>
      {role === "owner" ? (
        <p><Link className="btn-secondary" to={`/journey/stay/${stay.id}/edit`}>Edit stay details</Link></p>
      ) : null}
      {stay.notes.length ? stay.notes.map((note) => <p key={note}>{note}</p>) : null}
      {place.address ? <p className="lead">{place.address}</p> : <StatusNote>Address not on the booking yet.</StatusNote>}
      <PlaceActions place={place} />
      {splitHouseholds.length ? (
        <section className="banner">
          <p>
            {stay.nights} nights, held as an extra-night booking plus a three-night booking
            {splitHouseholds.length === trip.households.length ? " for each household" : ` for ${splitHouseholds.map((h) => h.label).join(" and ")}`}.
            They form one stay.
          </p>
          <p>{continuity}</p>
        </section>
      ) : stay.roomMoveRequired == null ? (
        <p className="muted">{continuity}</p>
      ) : (
        <p className="muted">{continuity}</p>
      )}
      <dl>
        <FactRow label="Check-in from" value={stay.checkInFrom} />
        <FactRow label="Official checkout" value={stay.checkOutBy} />
        {stay.plannedDepartureLocal ? (
          <FactRow
            label="Planned departure"
            value={`${stay.plannedDepartureLocal.slice(11, 16)} (our plan, not the hotel rule)`}
          />
        ) : null}
      </dl>
      {rooms.map((room) => (
        <article key={room.id} className="card">
          <h2 className="serif">{room.householdId ? `${trip.households.find((h) => h.id === room.householdId)?.label ?? ""} · ${room.roomName ?? bookingDisplayTitle(room, trip)}` : (room.roomName ?? bookingDisplayTitle(room, trip))}</h2>
          <p className="muted">
            {formatDateLabel(room.startDate ?? stay.startDate)} – {formatDateLabel(room.endDate ?? stay.endDate)}
          </p>
          <dl>
            <FactRow label="Confirmation" value={room.confirmation} copyable />
            <FactRow label="Agent itinerary" value={room.expediaItinerary} copyable />
            <FactRow label="Guests" value={room.occupancy ? `${room.occupancy.adults} adults, ${room.occupancy.children} children` : null} />
            <FactRow label="Cost" value={moneyLabel(room.cost, UNKNOWN_FACT)} />
            <FactRow label="Free cancellation until" value={room.freeCancellationUntil} />
            <FactRow label="Cancellation terms" value={room.cancellationTerms} />
          </dl>
          {room.notes.map((note) => <p key={note}>{note}</p>)}
          {room.breakfastIncluded ? <p>Breakfast included</p> : null}
          {room.beverageCredit ? (
            <p className="muted">{moneyLabel(room.beverageCredit)} beverage credit is a benefit, not subtracted from the total.</p>
          ) : null}
          <div className="row-actions">
            <Link to={`/bookings/${room.id}`}>Booking record</Link>
            {role === "owner" ? <Link to={`/journey/stay/${stay.id}/edit`}>Add details</Link> : null}
          </div>
        </article>
      ))}
      <DocumentList entityId={stay.id} files={filesFor(trip, stay.id, ...rooms.map((r) => r.id))} />
      <h3 className="section-title">Days</h3>
      <DateStrip dates={dates} hrefFor={(date) => `/journey/day/${date}`} />
    </main>
  );
}

export function DayPage() {
  const { date } = useParams();
  const { trip, save, saveState } = useTrip();
  const { role } = useSession();
  const [message] = useState<string | null>(null);
  if (!trip || !date) return <main className="page">Missing day.</main>;
  const current = trip;
  const day = date;
  const items = itemsForDate(current, day);
  const ideas = ideasForDate(current, day);
  const stay = current.stays.find((s) => s.startDate <= day && day < s.endDate)
    ?? current.stays.find((s) => s.endDate === day);
  const place = stay ? current.places.find((p) => p.id === stay.placeId) : undefined;
  const allDates = eachDateInclusive(current.trip.startDate, current.trip.endDate);
  const travel = isTravelDay(current, day);
  const stages = travel ? composeTravelDay(current, day) : [];

  return (
    <main className="page">
      <Back to={stay ? `/journey/stay/${stay.id}` : "/journey"} />
      <p className="kicker">{
        travel
          ? travelDayLabel(current, day)
          : (stay && place && stay.startDate !== day
            ? (stayNightLabel(stay, day, place.name) ?? place.name)
            : (place?.name ?? "Day"))
      }</p>
      <h1 className="page-title">{formatLongDate(day)}</h1>
      <DayNav dates={allDates} current={day} />
      <DateStrip dates={allDates} selected={day} hrefFor={(d) => `/journey/day/${d}`} />
      {stay ? (
        <p className="row-actions">
          <Link className="btn-secondary" to={`/journey/stay/${stay.id}`}>Stay details</Link>
          {role === "owner" ? <Link className="btn-ghost" to={`/journey/stay/${stay.id}/edit`}>Edit stay</Link> : null}
        </p>
      ) : null}
      {travel ? (
        <ol className="travel-chain">
          {stages.map((stage) => (
            <li key={stage.id} className={`travel-stage ${stage.unknown ? "unknown" : ""}`}>
              <p className="kicker">{stage.timeLabel ?? (stage.unknown ? "Unknown" : "Next")}</p>
              <h2 className="serif">{stage.title}</h2>
              {stage.detail ? <p>{stage.detail}</p> : null}
              {stage.href && stage.actionLabel ? <Link to={stage.href}>{stage.actionLabel}</Link> : null}
            </li>
          ))}
        </ol>
      ) : (
        <div className="timeline" style={{ marginTop: 16 }}>
          {items.length === 0 ? <p className="muted">A free day to explore.</p> : null}
          {items.map((item) => <TimelineItem key={item.id} item={item} />)}
        </div>
      )}
      {role === "owner" ? (
        <section style={{ marginTop: 20 }}>
          <h3 className="serif section-title">Add to this day</h3>
          <AddPlanChooser day={day} />
        </section>
      ) : null}
      {ideas.length ? (
        <section>
          <h3 className="serif section-title">Ideas nearby</h3>
          {ideas.map((idea) => {
            const ideaPlace = current.places.find((place) => place.id === idea.placeId);
            return (
              <article key={idea.id} className="card">
                <p>{idea.title}</p>
                {ideaPlace ? <p>{ideaPlace.name}</p> : null}
                {idea.notes ? <p>{idea.notes}</p> : null}
                <p className="muted">Not booked.</p>
                {role === "owner" ? (
                  <div className="row-actions">
                    <Link className="btn-secondary" to={`/journey/day/${day}/add?kind=idea&edit=${idea.id}`}>Edit</Link>
                    <Link className="btn-ghost" to={`/journey/day/${day}/add?kind=transfer&fromIdea=${idea.id}`}>Turn into a transfer</Link>
                    <button
                      className="btn-ghost"
                      type="button"
                      onClick={() => {
                        void save({ ...current, ideas: current.ideas.filter((row) => row.id !== idea.id) });
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      ) : null}
      {message || saveState === "saving" || saveState === "saved" ? (
        <div className="toast">
          <span>{saveState === "saving" ? "Saving…" : message ?? "Saved."}</span>
        </div>
      ) : null}
    </main>
  );
}

export function FlightPage() {
  const { id } = useParams();
  const { trip, save, saveState } = useTrip();
  const { role } = useSession();
  const [live, setLive] = useState<LivePayload | null>(null);
  const [pnr, setPnr] = useState("");
  const [seats, setSeats] = useState("");
  const [bags, setBags] = useState("");
  const [depTerminal, setDepTerminal] = useState("");
  const [arrTerminal, setArrTerminal] = useState("");
  const [flightNote, setFlightNote] = useState<string | null>(null);
  useEffect(() => {
    void fetchLive().then((raw) => setLive(raw as LivePayload)).catch(() => setLive(null));
  }, []);
  const flight = trip?.flights.find((f) => f.id === id);
  useEffect(() => {
    if (!flight) return;
    setPnr(flight.pnr ?? "");
    setSeats(flight.seats ?? "");
    setBags(flight.baggage ?? "");
    setDepTerminal(flight.departure.terminal ?? "");
    setArrTerminal(flight.arrival.terminal ?? "");
  }, [flight?.id, flight?.pnr, flight?.seats, flight?.baggage, flight?.departure.terminal, flight?.arrival.terminal]);
  if (!trip || !flight) return <main className="page"><Back /><p>Flight not found.</p></main>;
  const overnight = flight.departure.local.slice(0, 10) !== flight.arrival.local.slice(0, 10);
  const observation = flightObservation(live?.flights, flight.id);
  const pickup = linkedTransfer(trip, flight.id);
  const files = filesFor(trip, flight.id);
  const payload = observation?.payload as { status?: string; estimated_off?: string; estimated_on?: string } | undefined;

  return (
    <main className="page">
      <Back />
      <p className="kicker">{flight.airline}</p>
      <h1 className="serif">{flight.number}</h1>
      <section className="card board">
        <div className="leg">
          <p className="time">{flight.departure.local.slice(11, 16)}</p>
          <p>{flight.departure.airport}</p>
          <p className="muted">{formatLongDate(flight.departure.local.slice(0, 10), flight.departure.timezone)}</p>
          {flight.departure.terminal ? <p>Terminal {flight.departure.terminal}</p> : null}
          {flight.departure.gate ? <p>Gate {flight.departure.gate}</p> : null}
        </div>
        <div className="mid">
          <p>{formatDurationPretty(flight.departure.instant, flight.arrival.instant)}</p>
          <p>{flight.departure.airport} → {flight.arrival.airport}</p>
        </div>
        <div className="leg arrive">
          <p className="time">{flight.arrival.local.slice(11, 16)}{overnight ? " +1" : ""}</p>
          <p>{flight.arrival.airport}</p>
          <p className="muted">{formatLongDate(flight.arrival.local.slice(0, 10), flight.arrival.timezone)}</p>
          {flight.arrival.terminal ? <p>Terminal {flight.arrival.terminal}</p> : null}
        </div>
      </section>
      <p className="muted">{humanStatus(flight.evidenceStatus)}. Booked times stay on this card until a live estimate exists.</p>
      {observation ? (
        <section className="card">
          <p className="kicker">Live status</p>
          <p>{humanStatus(observation.status)}</p>
          {observation.error ? <p className="muted">{observation.error}</p> : null}
          {payload?.status ? <p>{String(payload.status)}</p> : null}
          {payload?.estimated_off ? <p className="muted">Estimated takeoff {payload.estimated_off}</p> : null}
          {payload?.estimated_on ? <p className="muted">Estimated landing {payload.estimated_on}</p> : null}
          {observation.fetchedAt ? <p className="muted">{minutesAgoLabel(observation.fetchedAt) ?? observation.fetchedAt.slice(0, 16).replace("T", " ")}</p> : null}
        </section>
      ) : null}
      <dl>
        <FactRow label="PNR" value={flight.pnr} copyable quiet={!flight.pnr} />
        <FactRow label="Seats" value={flight.seats} quiet={!flight.seats} />
        <FactRow label="Bags" value={flight.baggage} quiet={!flight.baggage} />
        <FactRow label="Payment" value={humanStatus(flight.paymentStatus)} quiet={flight.paymentStatus === "unknown"} />
      </dl>
      {role === "owner" ? (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            void save({
              ...trip,
              flights: trip.flights.map((row) =>
                row.id === flight.id
                  ? {
                      ...row,
                      pnr: pnr.trim() || null,
                      seats: seats.trim() || null,
                      baggage: bags.trim() || null,
                      departure: { ...row.departure, terminal: depTerminal.trim() || null },
                      arrival: { ...row.arrival, terminal: arrTerminal.trim() || null },
                    }
                  : row,
              ),
            }).then((result) => {
              setFlightNote(result.ok ? "Flight details saved." : result.message);
            });
          }}
        >
          <h2 className="serif">Flight details</h2>
          <p className="muted">PNR, seats, bags and terminals only. Import stays on Edit.</p>
          <label className="field">PNR<input value={pnr} onChange={(e) => setPnr(e.target.value)} /></label>
          <label className="field">Seats<input value={seats} onChange={(e) => setSeats(e.target.value)} /></label>
          <label className="field">Bags<input value={bags} onChange={(e) => setBags(e.target.value)} /></label>
          <label className="field">Departure terminal<input value={depTerminal} onChange={(e) => setDepTerminal(e.target.value)} /></label>
          <label className="field">Arrival terminal<input value={arrTerminal} onChange={(e) => setArrTerminal(e.target.value)} /></label>
          <button className="btn-secondary" type="submit" disabled={saveState === "saving"}>
            {saveState === "saving" ? "Saving…" : "Save flight details"}
          </button>
          {flightNote ? <p>{flightNote}</p> : null}
        </form>
      ) : null}
      {pickup ? (
        <section className="card pickup-panel">
          <p className="kicker">Pickup after this flight</p>
          <p>{pickup.pickupInstruction ?? pickup.title}</p>
          <Link className="btn" to={`/journey/transfer/${pickup.id}`}>Open pickup</Link>
        </section>
      ) : null}
      <p>
        <a className="btn-secondary" href={airlineStatusUrl(flight.airline, flight.number)} target="_blank" rel="noreferrer">
          Airline status page
        </a>
      </p>
      <DocumentList entityId={flight.id} files={files} />
    </main>
  );
}

export function TransferPage() {
  const { id } = useParams();
  const { trip, save, saveState } = useTrip();
  const { role } = useSession();
  const [copyState, setCopyState] = useState<string | null>(null);
  const [provider, setProvider] = useState("");
  const [costAmount, setCostAmount] = useState("");
  const [costCurrency, setCostCurrency] = useState<"THB" | "GBP">("THB");
  const [costShare, setCostShare] = useState<"split" | "chris" | "paul">("split");
  const [costError, setCostError] = useState<string | null>(null);
  const event = trip?.events.find((e) => e.id === id);
  const booking = trip?.bookings.find((b) => b.id === event?.bookingId);
  if (!trip || !event || event.kind !== "transfer") {
    return <main className="page"><Back /><p>Transfer not found.</p></main>;
  }
  const toPlace = placeById(trip, event.toPlaceId);
  const fromPlace = placeById(trip, event.fromPlaceId);
  const files = filesFor(trip, event.id, booking?.id);

  async function attachPackage(e: FormEvent) {
    e.preventDefault();
    if (!trip || !event || !provider.trim()) return;
    const applied = applyPlanBooking(trip, event, {
      provider: provider.trim(),
      confirmation: "",
      amount: costAmount,
      currency: costCurrency,
      share: costShare,
      paid: "unpaid",
      payerHouseholdId: "chris",
      paidDate: todayInZone(new Date(), trip.trip.homeTimezone),
    }, new Date().toISOString());
    if ("error" in applied) {
      setCostError(applied.error);
      return;
    }
    const result = await save(applied.doc);
    if (!result.ok) {
      setCostError(result.message);
      return;
    }
    setProvider("");
    setCostAmount("");
  }

  return (
    <main className="page">
      <Back to={`/journey/day/${event.date}`} />
      {booking ? <p className="kicker">{booking.provider}</p> : <p className="kicker">Transfer plan</p>}
      <h1 className="serif">{booking ? bookingDisplayTitle(booking, trip) : event.title}</h1>
      <p><StatusChip status={displayBookingStatus(event, booking)} /></p>
      {event.transferMethod ? <p className="muted">{humanStatus(event.transferMethod)}</p> : null}
      {!booking ? <p className="muted">Not linked to a cost package yet. Pickup and times can still be shared.</p> : null}
      {booking?.vehicle ? <p>{booking.vehicle}</p> : null}
      {role === "owner" ? (
        <p><Link className="btn-secondary" to={`/journey/day/${event.date}/add?edit=${event.id}`}>Edit plan</Link></p>
      ) : null}
      {event.pickupInstruction ? (
        <section className="card pickup-panel">
          <p className="kicker">Pickup</p>
          <p>{event.pickupInstruction}</p>
          {event.pickupMode === "when_ready" ? (
            <p className="muted">When ready, not a fixed appointment. Aircraft landed, bags collected, and ready for pickup are separate steps.</p>
          ) : null}
          <div className="row-actions">
          <button
            className="btn-secondary"
            type="button"
            onClick={() => void copyText(event.pickupInstruction ?? "").then((r) => setCopyState(r))}
          >
            Copy instructions
          </button>
          {(() => {
            const contact = booking?.contactId ? trip.contacts.find((c) => c.id === booking.contactId) : undefined;
            const url = contact?.phone ? whatsappDraftUrl(contact.phone, `Hello ${contact.name}, we are ready at the meeting point.`) : null;
            if (url) {
              return <a className="btn" href={url} target="_blank" rel="noreferrer">WhatsApp draft</a>;
            }
            if (booking && !booking.contactId) {
              return <p className="muted">No contact is linked to this transfer yet.</p>;
            }
            if (contact && !contact.phone) {
              return <p className="muted">This contact has no phone number yet.</p>;
            }
            return null;
          })()}
          </div>
          {copyState === "copied" ? <p>Copied.</p> : copyState === "denied" ? <p>Clipboard blocked on this device.</p> : null}
        </section>
      ) : null}
      <dl>
        <FactRow label="Date" value={`${formatLongDate(event.date)}${event.dateConfidence ? ` · ${humanStatus(event.dateConfidence)}` : ""}`} />
        <FactRow label="Pickup" value={fromPlace?.name ?? event.fromAirport ?? null} />
        <FactRow label="Destination" value={toPlace?.name ?? null} />
        <FactRow label="Planning times" value={
          event.plannedDeparture || event.plannedArrival || event.startLocal || event.endLocal
            ? `${clockHm(event.plannedDeparture ?? event.startLocal) ?? "to confirm"} → ${clockHm(event.plannedArrival ?? event.endLocal) ?? "to confirm"}`
            : event.pickupMode
              ? humanStatus(event.pickupMode)
              : null
        } quiet />
      </dl>
      {fromPlace ? <PlaceActions place={fromPlace} /> : null}
      {toPlace ? <PlaceActions place={toPlace} /> : null}
      {booking?.cost ? (
        <section className="card">
          <p className="kicker">Money</p>
          <p>Package {moneyLabel(booking.cost)} counted once for both legs when shared.</p>
          <p>{humanStatus(booking.allocationStatus)}</p>
          <Link className="btn-secondary" to={`/money/${booking.id}`}>Open money for this booking</Link>
        </section>
      ) : role === "owner" ? (
        <section className="card">
          <h2 className="serif">Link a package cost</h2>
          <p className="muted">Optional. Use when you have a supplier quote or confirmation.</p>
          <form onSubmit={(e) => void attachPackage(e)}>
            <label className="field">Operator
              <input value={provider} onChange={(ev) => setProvider(ev.target.value)} required />
            </label>
            <label className="field">Amount
              <input value={costAmount} onChange={(ev) => { setCostAmount(ev.target.value); setCostError(null); }} inputMode="decimal" required />
            </label>
            {costError ? <p className="banner crit">{costError}</p> : null}
            <label className="field">Currency
              <select value={costCurrency} onChange={(ev) => setCostCurrency(ev.target.value as "THB" | "GBP")}>
                <option value="THB">THB</option>
                <option value="GBP">GBP</option>
              </select>
            </label>
            <label className="field">Who is this cost for?
              <select value={costShare} onChange={(ev) => setCostShare(ev.target.value as "split" | "chris" | "paul")}>
                <option value="split">Split to confirm</option>
                <option value="chris">Chris only</option>
                <option value="paul">Paul only</option>
              </select>
            </label>
            <button className="btn-secondary" type="submit">Save package</button>
          </form>
        </section>
      ) : null}
      {event.notes.map((note) => <p key={note}>{note}</p>)}
      {booking?.notes.map((note) => <p key={note} className="muted">{note}</p>)}
      {booking ? <ContactLine trip={trip} contactId={booking.contactId} /> : null}
      <DocumentList entityId={event.id} files={files} />
      {role === "owner" ? (
        <OwnerRemoveEvent trip={trip} eventId={event.id} save={save} busy={saveState === "saving"} />
      ) : null}
    </main>
  );
}

export function EventPage() {
  const { id } = useParams();
  const { trip, save, saveState } = useTrip();
  const { role } = useSession();
  const event = trip?.events.find((e) => e.id === id);
  const booking = trip?.bookings.find((b) => b.id === event?.bookingId);
  const [draft, setDraft] = useState(() =>
    draftFromBooking(booking, trip ? todayInZone(new Date(), trip.trip.homeTimezone) : ""),
  );
  const [costError, setCostError] = useState<string | null>(null);
  if (!trip || !event) return <main className="page"><Back /><p>Event not found.</p></main>;
  const place = trip.places.find((p) => p.id === (event.meetingPlaceId ?? event.toPlaceId));
  const files = filesFor(trip, event.id, booking?.id);
  const needsBooking = event.kind === "excursion" && (!booking || !booking.cost);

  async function saveBooking(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (!trip || !event) return;
    const applied = applyPlanBooking(trip, { ...event, planStatus: event.planStatus ?? "booked" }, draft, new Date().toISOString());
    if ("error" in applied) {
      setCostError(applied.error);
      return;
    }
    const result = await save(applied.doc);
    if (!result.ok) {
      setCostError(result.message);
      return;
    }
    setCostError(null);
  }

  return (
    <main className="page">
      <Back to={`/journey/day/${event.date}`} />
      <h1 className="serif">{event.title ?? booking?.title}</h1>
      {role === "owner" ? (
        <p><Link className="btn-secondary" to={`/journey/day/${event.date}/add?edit=${event.id}`}>Edit plan</Link></p>
      ) : null}
      <p><StatusChip status={displayBookingStatus(event, booking)} /></p>
      <p>{formatLongDate(event.date)}{clockHm(event.startLocal ?? event.plannedDeparture) ? ` · ${clockHm(event.startLocal ?? event.plannedDeparture)}` : " · time to confirm"}</p>
      {timingConfidenceMessage(event) ? <StatusNote tone="warn">{timingConfidenceMessage(event)}</StatusNote> : null}
      <p>Meeting: {place?.name ?? UNKNOWN_FACT}{event.meetingNote ? ` · ${event.meetingNote}` : ""}</p>
      {place ? <PlaceActions place={place} /> : null}
      {visibleEventNotes(event.notes).map((note) => <p key={note}>{note}</p>)}
      {event.stops.map((stop) => (
        <p key={`${stop.time}-${stop.description}`}>{stop.time ?? "Flexible"} · {stop.description}</p>
      ))}
      {booking?.dietaryNotes?.map((note) => <p key={note}>{note}</p>)}
      {booking?.cost ? (
        <section className="card">
          <p className="kicker">Money</p>
          <p>{moneyLabel(booking.cost)}</p>
          {booking.priceBasis ? (
            <p>{priceBasisSummary(booking.priceBasis, booking.cost?.currency ?? "THB")}</p>
          ) : null}
          {booking.allocations.length ? (
            <ul className="plain-list">
              {booking.allocations.map((row) => (
                <li key={row.householdId}>
                  {trip.households.find((h) => h.id === row.householdId)?.label ?? row.householdId}
                  {": "}
                  {booking.cost ? formatMinor(booking.cost.currency, row.amountMinor) : row.amountMinor}
                </li>
              ))}
            </ul>
          ) : null}
          {booking.allocationStatus ? <p className="muted">{humanStatus(booking.allocationStatus)}</p> : null}
          <div className="row-actions">
            <Link className="btn-secondary" to={`/bookings/${booking.id}`}>Open in Bookings</Link>
            <Link className="btn-secondary" to={`/money/${booking.id}`}>Open money</Link>
            {role === "owner" ? <Link className="btn-secondary" to={`/money/${booking.id}?edit=1`}>Edit amounts</Link> : null}
          </div>
        </section>
      ) : role === "owner" && needsBooking ? (
        <form onSubmit={(e) => void saveBooking(e)}>
          <PlanBookingFields trip={trip} draft={draft} onChange={setDraft} existing={booking} />
          {costError ? <p className="banner crit">{costError}</p> : null}
          <button className="btn" type="submit" disabled={saveState === "saving"}>
            {saveState === "saving" ? "Saving…" : "Save booking"}
          </button>
        </form>
      ) : booking ? (
        <section className="card">
          <p className="kicker">Money</p>
          <p className="muted">In Bookings, with no cost recorded yet.</p>
          {booking.allocations.length ? (
            <ul className="plain-list">
              {booking.allocations.map((row) => (
                <li key={row.householdId}>
                  {trip.households.find((h) => h.id === row.householdId)?.label ?? row.householdId}
                  {": "}
                  {booking.cost ? formatMinor(booking.cost.currency, row.amountMinor) : row.amountMinor}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="row-actions">
            <Link className="btn-secondary" to={`/bookings/${booking.id}`}>Open in Bookings</Link>
            <Link className="btn-secondary" to={`/money/${booking.id}`}>Open money</Link>
            {role === "owner" ? <Link className="btn-secondary" to={`/money/${booking.id}?edit=1`}>Edit amounts</Link> : null}
          </div>
        </section>
      ) : null}
      <DocumentList entityId={event.id} files={files} />
      {role === "owner" ? (
        <OwnerRemoveEvent trip={trip} eventId={event.id} save={save} busy={saveState === "saving"} />
      ) : null}
    </main>
  );
}

function OwnerRemoveEvent({
  trip,
  eventId,
  save,
  busy,
}: {
  trip: TripDocument;
  eventId: string;
  save: (next: TripDocument) => Promise<SaveResult>;
  busy: boolean;
}) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const preview = previewEventRemoval(trip, eventId);
  if ("error" in preview) return null;
  const event = trip.events.find((row) => row.id === eventId);
  return (
    <RemoveRecord
      preview={preview}
      busy={busy}
      error={error}
      onConfirm={() => {
        const result = removeEventFromTrip(trip, eventId);
        if (!result.ok) {
          setError(result.error);
          return;
        }
        void save(result.doc).then((saved) => {
          if (!saved.ok) {
            setError(saved.message);
            return;
          }
          navigate(`/journey/day/${event?.date ?? ""}`);
        });
      }}
    />
  );
}

export function DriverPage() {
  const { id } = useParams();
  const { trip } = useTrip();
  const place = trip?.places.find((p) => p.id === id);
  if (!place) return <main className="page"><Back to="/map" /><p>Place not found.</p></main>;
  return (
    <main className="page">
      <Back to="/map" />
      <DriverCard place={place} />
    </main>
  );
}

export function AllergyCardPage() {
  const { trip } = useTrip();
  if (!trip) return <main className="page">Loading…</main>;
  return (
    <main className="page">
      <Link className="btn-ghost" to="/essentials">Back</Link>
      <AllergyCard trip={trip} />
    </main>
  );
}

function ContactLine({ trip, contactId }: { trip: TripDocument; contactId?: string | null }) {
  const contact = trip.contacts.find((c) => c.id === contactId);
  if (!contact) return <p className="muted">Contact details to add.</p>;
  return (
    <div className="contact-row">
      <div>
        <p>{contact.name}</p>
        <p className="muted">{contact.role}</p>
      </div>
      {contact.phone ? <a className="btn" href={`tel:${contact.phone}`}>Call</a> : <span className="muted">No number yet</span>}
    </div>
  );
}
